import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/brand/brand/apps/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { PropTableValues } from '../../src/components';
import { Link } from 'gatsby';
import { AnimationVariants, Label } from '@primer/react-brand';
import { supportedComponents } from '../../scripts/components-with-animation';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3>{`Animate prop`}</h3>
    <p>{`Certain Primer Brand components include animation presets. These components are:`}</p>
    <p>
  <PropTableValues values={supportedComponents} commaSeparated removeApostrophes mdxType="PropTableValues" />
    </p>
    <p>{`You can use the `}<inlineCode parentName="p">{`animate`}</inlineCode>{` prop available on these components to apply one of the available animation presets directly to the component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Image animate="fade-in" />
`}</code></pre>
    <h3>{`Animate component`}</h3>
    <p>{`You can alternatively compose animations into your React application using the `}<inlineCode parentName="p">{`Animate`}</inlineCode>{` wrapper component. This can be useful in situations where you need to animate native HTML elements, or components that don't support the `}<inlineCode parentName="p">{`animate`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {Animate} from '@primer/react-brand'
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<AnimationProvider>
  <Animate animate="fade-in">
    <Text>This text will fade-in</Text>
  </Animate>
</AnimationProvider>
`}</code></pre>
    <h3>{`AnimationProvider`}</h3>
    <p>{`An `}<inlineCode parentName="p">{`AnimationProvider`}</inlineCode>{` is first required to enable animations on the page. This component should wrap specific parts of your application code, or the entire app.
The `}<inlineCode parentName="p">{`AnimationProvider`}</inlineCode>{` assumes responsibility for triggering animations and automatically applying effects such as staggering.`}</p>
    <p>{`By default, the `}<inlineCode parentName="p">{`AnimationProvider`}</inlineCode>{` will stagger animations in order of DOM appearance. This behavior can be turned off by setting `}<inlineCode parentName="p">{`autoStaggerChildren`}</inlineCode>{` to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <p>{`The increment delta can also be increased or decreased using `}<inlineCode parentName="p">{`staggerDelayIncrement`}</inlineCode>{`, which is set to 100ms by default.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {AnimationProvider} from '@primer/react-brand'
`}</code></pre>
    <h3>{`Animation variants`}</h3>
    <p>{`Supported animations types include:`}</p>
    <PropTableValues values={AnimationVariants} commaSeparated removeApostrophes mdxType="PropTableValues" />
    <br />
    <br />
    <p>{`To use a specific animation variant, simply pass the `}<inlineCode parentName="p">{`animate`}</inlineCode>{` prop with the desired variant. This value can either be a `}<inlineCode parentName="p">{`string`}</inlineCode>{` or and `}<inlineCode parentName="p">{`Object`}</inlineCode>{`. The latter provides more granular control over the animation delay, durations and easing.`}</p>
    <p>{`Use the example below to try each animation variant.`}</p>
    <br />
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const App = () => {
  const [animationVariant, setAnimationVariant] = React.useState('fade-in')

  const handleAnimationChange = (event) =>
    setAnimationVariant(event.target.value)

  return (
    <AnimationProvider key={animationVariant}>
      <Stack
        direction="horizontal"
        alignItems="center"
        justifyContent="center"
        style={{height: 200}}
      >
        <Image
          animate={{
            variant: animationVariant,
          }}
          src="https://via.placeholder.com/150x75/D0D7DE/D0D7DE.png"
          alt="placeholder with gray background and no foreground text"
        />
        <Image
          animate={{
            variant: animationVariant,
          }}
          src="https://via.placeholder.com/150x75/D0D7DE/D0D7DE.png"
          alt="placeholder with gray background and no foreground text"
        />
        <Image
          animate={{
            variant: animationVariant,
          }}
          src="https://via.placeholder.com/150x75/D0D7DE/D0D7DE.png"
          alt="placeholder with gray background and no foreground text"
        />
      </Stack>

      <Stack direction="horizontal" gap="spacious" justifyContent="center">
        <FormControl>
          <FormControl.Label>Animation</FormControl.Label>
          <Select
            defaultValue={animationVariant}
            onChange={handleAnimationChange}
          >
            {AnimationVariants.map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </AnimationProvider>
  )
}

render(App)
`}</code></pre>
    <h2>{`Examples`}</h2>
    <p>{`Animations should be used sparingly. The examples below demonstrate some valid use-cases.`}</p>
    <h3>{`River`}</h3>
    <p>{`Apply animation to `}<inlineCode parentName="p">{`River.Content`}</inlineCode>{` instead of the entire element.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<AnimationProvider>
  <River>
    <River.Visual>
      <img
        src="https://via.placeholder.com/600x400/f5f5f5/f5f5f5.png"
        alt="placeholder, blank area with an off-white background color"
      />
    </River.Visual>
    <River.Content animate="slide-in-right">
      <Heading>Heading</Heading>
      <Text>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sapien sit
        ullamcorper id. Aliquam luctus sed turpis felis nam pulvinar risus
        elementum.
      </Text>
      <Link href="#">Call to action</Link>
    </River.Content>
  </River>
</AnimationProvider>
`}</code></pre>
    <h3>{`Cards and staggering`}</h3>
    <p><inlineCode parentName="p">{`AnimationProvider`}</inlineCode>{` automatically applies a delay increment to all `}<inlineCode parentName="p">{`children`}</inlineCode>{` that have `}<inlineCode parentName="p">{`animation`}</inlineCode>{` presets defined. Extend the duration of each delay using `}<inlineCode parentName="p">{`staggerDelayIncrement`}</inlineCode>{`.`}</p>
    <p>{`Alternatively, use `}<inlineCode parentName="p">{`delay`}</inlineCode>{` to stagger animations on adjacent elements if this feature is toggled off.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<AnimationProvider staggerDelayIncrement={200}>
  <Grid>
    <Grid.Column
      span={{
        xsmall: 12,
        large: 6,
      }}
    >
      <Animate animate="scale-in-up">
        <Card href="https://github.com">
          <Card.Icon icon={<CopilotIcon />} color="indigo" hasBackground />
          <Card.Label>Limited</Card.Label>
          <Card.Heading>
            Collaboration is the key to DevOps success
          </Card.Heading>
          <Card.Description>
            Everything you need to know about getting started with GitHub
            Actions.
          </Card.Description>
        </Card>
      </Animate>
    </Grid.Column>
    <Grid.Column
      span={{
        xsmall: 12,
        large: 6,
      }}
    >
      <Animate animate="scale-in-up">
        <Card href="https://github.com">
          <Card.Icon icon={RocketIcon} hasBackground color="blue" />
          <Card.Label>Limited</Card.Label>
          <Card.Heading>GitHub Actions cheat sheet and more</Card.Heading>
          <Card.Description>
            In a recent study, 70% of organizations reported they had adopted
            DevOps.
          </Card.Description>
        </Card>
      </Animate>
    </Grid.Column>
  </Grid>
</AnimationProvider>
`}</code></pre>
    <p><a parentName="p" {...{
        "href": "/brand/storybook/?path=/story/components-animations--playground"
      }}>{`See Storybook for more examples of animation`}</a>{`.`}</p>
    <h2>{`Props`}</h2>
    <h3>{`AnimationProvider `}<Label mdxType="Label">{`Required`}</Label></h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The children to render within the `}<inlineCode parentName="td">{`AnimationProvider`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disableAnimations`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Prevents animations from running inside the provider`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`animationTrigger`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><PropTableValues values={['click', 'on-visible']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'on-visible'`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Controls the trigger method for the animation. One of `}<inlineCode parentName="td">{`click`}</inlineCode>{` or `}<inlineCode parentName="td">{`on-visible`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`visibilityOptions`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><PropTableValues values={['bottom-of-screen', 'middle-of-screen', 'about-to-leave', 'number']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'bottom-of-screen'`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Controls the intersection observer options for the animation.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`runOnce`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Will persist the animation end-state after the animation has completed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`autoStaggerChildren`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Will stagger the animations of the children using an incrementing delay`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`staggerDelayIncrement`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`100`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Stagger delay increment. Should be used alongside `}<inlineCode parentName="td">{`autoStaggerChildren`}</inlineCode>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Animate`}</h3>
    <p><inlineCode parentName="p">{`animate`}</inlineCode>{` is a prop available on the following components:`}</p>
    <p>
  <PropTableValues values={supportedComponents} commaSeparated removeApostrophes mdxType="PropTableValues" />
    </p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`animate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><PropTableValues values={AnimationVariants} addLineBreaks mdxType="PropTableValues" />{` `}{`|`}{` `}<inlineCode parentName="td">{`Object`}</inlineCode></td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      